import React, { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import SearchResult from './SearchResult';
import noResultsImage from './no-results.png';
import useSearchStore from './useSearchStore';

export const ITEMS_PER_PAGE = 25;

interface SearchResultsProps {
  itemsPerPage?: number;
  showScore?: boolean;
  truncateResults?: boolean;
}

const SearchResults = ({
  itemsPerPage = ITEMS_PER_PAGE,
  showScore = false,
  truncateResults = false,
}: SearchResultsProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const isLoading = useSearchStore(state => state.isLoading);
  const results = useSearchStore(state => state.results);
  const numResults = useSearchStore(state => state.numResults);

  const offset = currentPage * itemsPerPage;
  const currentPageData = results.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(results.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(0);
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-64 tw-w-full">
        <i aria-hidden="true" className="fa fa-spinner fa-spin" />
      </div>
    );
  }

  if (numResults === undefined || numResults === 0) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-pt-16 tw-w-full">
        {numResults === undefined && (
          'Enter a search term and click the magnifying glass or press enter to begin searching.'
        )}
        {numResults === 0 && (
          <div className="tw-text-center">
            <img alt="No matching results" className="tw-w-1/3" src={noResultsImage} />
            <h1 className="tw-mt-4">No matching results</h1>
            <span className="tw-text-sm">Try using a different search term</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tw-w-full" role="list">
      {currentPageData.map((result, index) => (
        <SearchResult
          key={`${result.klass}-${result.id}-${currentPage}-${result.program}`}
          position={index + 1}
          showScore={showScore}
          truncateResults={truncateResults}
          {...result}
        />
      ))}
      {pageCount > 1 && (
        <ReactPaginate
          activeClassName="tw-bg-tci-purple tw-rounded-sm"
          activeLinkClassName="tw-text-white hover:tw-text-white"
          breakClassName="tw-flex tw-items-center"
          containerClassName="tw-flex tw-float-right"
          nextClassName={`tw-flex tw-items-center ${currentPage >= pageCount - 1 && 'tw-invisible'}`}
          nextLabel={<i className="fa fa-chevron-right" />}
          pageClassName="tw-flex tw-justify-center tw-items-center tw-px-2 tw-h-6"
          pageCount={pageCount}
          previousClassName={`tw-flex tw-items-center ${currentPage <= 0 && 'tw-invisible'}`}
          previousLabel={<i className="fa fa-chevron-left" />}
          onPageChange={e => setCurrentPage(e.selected)}
        />
      )}
    </div>
  );
};

export default SearchResults;
